import { useState } from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import Button from "../components/layout/Button";
import BackToUsersButton from "../components/userManagement/backToUsersButton";
import GroupPicker from "../components/userManagement/groupPicker";
import { validateUserFields } from "../components/userManagement/userValidation";
import { batchUpdateMemberships, createUser } from "../services/accountManagement.service";
import { isHttpError } from "../services/apiService";

const CreateUser: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [groups, setGroups] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fullName = `${givenName.trim()} ${familyName.trim()}`.trim();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationError = validateUserFields({ email, givenName, familyName });
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const user = await createUser({
        email: email.trim(),
        given_name: givenName.trim(),
        family_name: familyName.trim(),
        name: fullName,
      });

      if (groups.length > 0) {
        await batchUpdateMemberships(
          groups.map((group_name) => ({
            username: user.username,
            group_name,
            action: "add" as const,
          })),
        );
      }

      navigate("/admin/users");
    } catch (error) {
      if (isHttpError(error) && error.response.status === 409) {
        setErrorMessage("A user with that email already exists.");
      } else if (isHttpError(error)) {
        setErrorMessage(`Unable to create user (${String(error.response.status)}).`);
      } else {
        setErrorMessage("Unable to create user.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-3">
        <BackToUsersButton />
        <h1 className="mb-0">New User</h1>
      </div>

      <Form onSubmit={(e) => void handleSubmit(e)}>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="create-user-email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="create-user-given-name">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={givenName}
                onChange={(e) => {
                  setGivenName(e.target.value);
                }}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="create-user-family-name">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={familyName}
                onChange={(e) => {
                  setFamilyName(e.target.value);
                }}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="create-user-name">
              <Form.Label>Full Name</Form.Label>
              <Form.Control type="text" value={fullName} disabled readOnly />
            </Form.Group>

            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create User"}
            </Button>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Access Groups</Form.Label>
              <GroupPicker selected={groups} onChange={setGroups} disabled={submitting} />
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CreateUser;
